/**
 * Sync Helper
 * 
 * Responsible for managing synchronization operations between
 * local database and server.
 */

import recordManager from './recordManager';
import tablesMetadataService from '../tablesMetadataService';
import { LAST_SYNC_TIME_KEY } from './dbStorageHelper';

class SyncHelper {
    /**
     * Gets the timestamp of the last successful sync operation
     * @returns {string|null} ISO string timestamp of last sync or null
     */
    getLastSyncTime() {
        return localStorage.getItem(LAST_SYNC_TIME_KEY);
    }

    /**
     * Sets the timestamp of the last successful sync operation
     * @param {string|Date} timestamp - The timestamp to set
     */
    setLastSyncTime(timestamp) {
        const isoTimestamp = timestamp instanceof Date 
            ? timestamp.toISOString() 
            : timestamp;
        
        localStorage.setItem(LAST_SYNC_TIME_KEY, isoTimestamp);
    }

    /**
     * Gets all local changes that need to be synced to the server
     * @param {IDBDatabase} db - Database instance
     * @returns {Promise<Object>} Changes grouped by table name
     */
    async getLocalChanges(db) {
        // Get last sync time with fallback to a very old date if not available
        let lastSyncTime = this.getLastSyncTime();
        // Ensure we have a valid timestamp - use a default old date if not
        if (!lastSyncTime || typeof lastSyncTime !== 'string') {
            lastSyncTime = '2000-01-01T00:00:00.000Z'; // Default to very old date
        }
        
        const changes = {};
        
        // Get all syncable tables from tablesMetadataService
        const syncableTables = tablesMetadataService.GetAllSyncableTables();

        // For each table, get records modified since last sync
        for (const tableName of syncableTables) {
            try {
                const records = await recordManager.getRecordsModifiedSince(db, tableName, lastSyncTime);
                if (records.length > 0) {
                    changes[tableName] = records;
                }
            } catch (error) {
                if (error.name === 'NotFoundError') {
                    // Handle empty/new table case
                    continue;
                }
                throw error;
            }
        }

        return changes;
    }

    /**
     * Gets the count of unread notifications
     * @param {IDBDatabase} db - Database instance
     * @returns {Promise<number>} Count of unread notifications
     */
    async getUnreadNotificationsCount(db) {
        // Early return if the database is in cleanup mode or not initialized
        if (window.dbCleanupInProgress || !db) {
            // console.log('[dbStorageService] Database not initialized or in cleanup, returning 0 notifications');
            return 0;
        }

        try {
            try {
                const transaction = db.transaction('Notification', 'readonly');
                const store = transaction.objectStore('Notification');
                const records = await new Promise((resolve, reject) => {
                    const request = store.getAll();
                    request.onsuccess = () => resolve(request.result);
                    request.onerror = () => reject(request.error);
                });

                return records.filter(record => !record.isDeleted && !record.isRead).length;
            } catch (error) {
                if (error.name === 'NotFoundError' || error.message.includes('Notification')) {
                    return 0; // Return 0 if table doesn't exist
                }
                
                // If we're getting InvalidStateError or any other error during logout, just return 0
                if (error.name === 'InvalidStateError' || window.dbCleanupInProgress) {
                    // console.log('[dbStorageService] Database in invalid state, returning 0 notifications');
                    return 0;
                }
                
                throw error;
            }
        } catch (error) {
            // During logout/cleanup, don't let errors propagate
            console.warn('[dbStorageService] Error getting unread count, returning 0:', error.message);
            return 0;
        }
    }
}

export default new SyncHelper(); 